/**
 * ImportToDriveModal
 *
 * Flow:
 *   1. Auth    – Google OAuth token
 *   2. Detect  – auto-find "compressed" subfolder (or use root if no subfolders)
 *                → on success skip straight to folder browser, no confirm screen
 *   3. Confirm – manual input / subfolder picker (only when auto-detect fails)
 *   4. Browse  – in-app folder browser (mobile + desktop). Tap folder = pick;
 *                chevron = enter deeper. Faster than Google Picker SDK.
 *   5. Ready   – confirm source + destination, start background job
 *   6. Queued  – job running on server, user can close modal
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import {
  useGoogleDrive,
  extractFolderIdFromUrl,
  getFolder,
  getFolderWithParentName,
  listSubfolders,
  type DriveFolder,
} from "../hooks/useGoogleDrive.js";
import { startImportDrive } from "../lib/api.js";
import { readQuickFolder, writeQuickFolder } from "../lib/quick-folder.js";
import { showToast } from "./Toaster.js";

const LAST_FOLDER_KEY = "kra_last_drive_folder";

// ── Types ─────────────────────────────────────────────────────────────────────

type Step =
  | "auth-checking" // attempting silent token refresh
  | "auth"          // silent failed → show manual sign-in button
  | "detecting"
  | "confirm-folder"
  | "folder-browser"
  | "ready"
  | "importing"     // quick import in flight (no manual confirm screen)
  | "error";

interface Props {
  photo: PhotoRecord;
  onClose: () => void;
  onSaved: (adminDriveLink: string) => void;
  /** Quick mode: skip the destination picker and dump straight into the
   *  remembered quick folder (set once on first use). */
  quick?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ImportToDriveModal({ photo, onClose, onSaved, quick = false }: Props) {
  const { accessToken: existingToken, requestToken, requestTokenSilent } = useGoogleDrive();

  // If our cache has a token, jump straight to detecting. Otherwise start
  // in `auth-checking` and try a silent refresh before showing the manual
  // sign-in button — Google's browser session usually still has the user
  // signed in even when our 1h access-token cache has expired.
  const [step, setStep] = useState<Step>(existingToken ? "detecting" : "auth-checking");
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(existingToken);

  // Silent token refresh on mount — no UI visible. Falls back to the
  // manual sign-in button (`step="auth"`) if Google requires interaction.
  useEffect(() => {
    if (existingToken) return;
    let cancelled = false;
    requestTokenSilent().then((t) => {
      if (cancelled) return;
      if (t) {
        setToken(t);
        setStep("detecting");
      } else {
        setStep("auth");
      }
    });
    return () => { cancelled = true; };
    // Run once on mount — `requestTokenSilent` is stable from useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Source folder
  const [sourceFolderId, setSourceFolderId] = useState<string | null>(null);
  const [sourceFolderName, setSourceFolderName] = useState<string>("");
  const [manualLink, setManualLink] = useState("");
  const [foundAuto, setFoundAuto] = useState(false);
  /** Subfolders found in the root Drive folder when auto-detect failed —
   *  shown as a clickable picker so admin doesn't have to paste a link. */
  const [availableSubfolders, setAvailableSubfolders] = useState<DriveFolder[]>([]);

  // Destination folder
  const [destFolderId, setDestFolderId] = useState<string | null>(null);
  const [destFolderName, setDestFolderName] = useState<string>("");

  // In-app folder browser (mobile)
  const [browserStack, setBrowserStack] = useState<DriveFolder[]>([]);
  const [browserFolders, setBrowserFolders] = useState<DriveFolder[]>([]);
  const [browserLoading, setBrowserLoading] = useState(false);

  // Folder name (editable, defaults to chatName).
  // We mirror the state into a ref so handleStartImport always reads the
  // latest value at click time — guarantees no stale-closure surprises
  // even if the deps array drifts in the future.
  const [folderName, setFolderName] = useState(photo.chatName);
  const folderNameRef = useRef(photo.chatName);

  // Submission
  const [submitting, setSubmitting] = useState(false);

  const didMountRef = useRef(false);
  // Guards the detect effect so its async body runs once per entry into the
  // "detecting" step — needed because quick mode reads the latest callbacks
  // off closures rather than the dep array.
  const detectRanRef = useRef(false);
  useEffect(() => {
    if (step !== "detecting") detectRanRef.current = false;
  }, [step]);

  // ── Step 1: Auth ────────────────────────────────────────────────────────────
  const handleAuth = useCallback(async () => {
    setError(null);
    try {
      const t = await requestToken();
      setToken(t);
      setStep("detecting");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi đăng nhập Google");
    }
  }, [requestToken]);

  // ── Core: fire the background import with explicit args ──────────────────────
  // Used by both the manual "ready" screen and the quick-import auto flow.
  // On success the modal closes itself and a corner toast says the copy is
  // running (Discord reports when it's done) — nothing left to dismiss.
  // Returns false on failure (error set, caller decides where to route).
  const runImport = useCallback(
    async (opts: {
      token: string;
      sourceFolderId: string;
      destFolderId: string;
      folderName?: string;
    }): Promise<boolean> => {
      setSubmitting(true);
      setError(null);
      try {
        await startImportDrive({
          oauthToken: opts.token,
          sourceFolderId: opts.sourceFolderId,
          destFolderId: opts.destFolderId,
          photoId: photo.id,
          chatName: photo.chatName,
          folderName: opts.folderName,
        });
        onSaved("");
        showToast(`🚀 Đang copy "${photo.chatName}" vào Drive — Discord sẽ báo khi xong`);
        onClose();
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi khởi động import");
        setSubmitting(false);
        return false;
      }
    },
    [photo.id, photo.chatName, onSaved, onClose],
  );

  // Source folder is now known. In quick mode with a remembered destination,
  // start the import immediately; otherwise fall through to the picker (which
  // doubles as quick-folder setup the first time).
  const applySourceAndProceed = useCallback(
    async (srcId: string, srcName: string) => {
      setSourceFolderId(srcId);
      setSourceFolderName(srcName);
      const qf = quick ? readQuickFolder() : null;
      if (quick && qf && token) {
        setDestFolderId(qf.id);
        setDestFolderName(qf.name);
        setStep("importing");
        const ok = await runImport({
          token,
          sourceFolderId: srcId,
          destFolderId: qf.id,
          folderName: folderNameRef.current.trim() || undefined,
        });
        if (!ok) setStep("error");
        return;
      }
      setStep("folder-browser");
    },
    [quick, token, runImport],
  );

  // ── Step 2: Auto-detect source folder ───────────────────────────────────────
  useEffect(() => {
    if (step !== "detecting" || !token || detectRanRef.current) return;
    detectRanRef.current = true;

    const detect = async () => {
      try {
        const folderId = extractFolderIdFromUrl(photo.driveLink);
        if (!folderId) {
          setFoundAuto(false);
          setStep("confirm-folder");
          return;
        }

        const subfolders = await listSubfolders(folderId, token);
        // Match "compressed", "compressed file", "compres", etc — but NOT
        // "uncompressed" / "original" / "full" / "raw".
        const COMPRESSED_RE = /compres/i;
        const EXCLUDE_RE = /(uncompres|original|full|raw)/i;
        const compressed = subfolders.find(
          (f) => COMPRESSED_RE.test(f.name) && !EXCLUDE_RE.test(f.name)
        );

        if (compressed) {
          setFoundAuto(true);
          await applySourceAndProceed(compressed.id, compressed.name);
        } else if (subfolders.length === 0) {
          // No subfolders → the driveLink itself contains photos directly
          const rootFolder = await getFolder(folderId, token);
          setFoundAuto(true);
          await applySourceAndProceed(folderId, rootFolder.name);
        } else {
          // Auto-detect failed but the folder DOES have subfolders — surface
          // them so the admin can pick one without copy-pasting a link.
          setAvailableSubfolders(subfolders);
          setFoundAuto(false);
          setStep("confirm-folder");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi khi tìm thư mục");
        setStep("error");
      }
    };

    void detect();
    // applySourceAndProceed is read off the closure and the body is guarded by
    // detectRanRef, so it intentionally stays out of the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, token, photo.driveLink]);

  // ── Confirm source folder ────────────────────────────────────────────────────
  const handleManualConfirm = useCallback(() => {
    const id = extractFolderIdFromUrl(manualLink) ?? manualLink.trim();
    if (!id) { setError("Link không hợp lệ"); return; }
    setFoundAuto(false);
    void applySourceAndProceed(id, "(thư mục đã chọn)");
  }, [manualLink, applySourceAndProceed]);

  /** Admin picks a subfolder from the auto-listed candidates. */
  const handlePickSubfolder = useCallback((folder: DriveFolder) => {
    void applySourceAndProceed(folder.id, folder.name);
  }, [applySourceAndProceed]);

  const handleConfirmAuto = useCallback(() => {
    if (sourceFolderId) void applySourceAndProceed(sourceFolderId, sourceFolderName);
    else setStep("folder-browser");
  }, [sourceFolderId, sourceFolderName, applySourceAndProceed]);

  // ── Step 3: In-app folder browser (mobile + desktop) ────────────────────────
  useEffect(() => {
    if (step !== "folder-browser" || !token || didMountRef.current) return;
    didMountRef.current = true;

    const init = async () => {
      setBrowserLoading(true);
      setError(null);

      const savedId = localStorage.getItem(LAST_FOLDER_KEY);
      let startId = "root";
      let startName = "My Drive";

      if (savedId) {
        try {
          const info = await getFolder(savedId, token);
          startId = info.id;
          startName = info.name;
        } catch { /* fall back to root */ }
      }

      try {
        const folders = await listSubfolders(startId, token);
        setBrowserStack([{ id: startId, name: startName }]);
        setBrowserFolders(folders);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setBrowserLoading(false);
    };

    void init();
  }, [step, token]);

  // Reset didMountRef when leaving folder-browser
  useEffect(() => {
    if (step !== "folder-browser") didMountRef.current = false;
  }, [step]);

  const handleBrowserEnter = useCallback(async (folder: DriveFolder) => {
    if (!token) return;
    setBrowserLoading(true);
    setError(null);
    try {
      const folders = await listSubfolders(folder.id, token);
      setBrowserStack((prev) => [...prev, folder]);
      setBrowserFolders(folders);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
    }
    setBrowserLoading(false);
  }, [token]);

  /**
   * Go up one level. Two paths:
   *  - We descended via chevrons this session → just pop the stack (no API).
   *  - We started deep at the saved folder (stack length 1) → resolve the
   *    real parent from Drive so the admin isn't stuck at the start folder.
   */
  const handleBrowserBack = useCallback(async () => {
    if (!token) return;

    // Fast path: in-session history exists → pop locally.
    if (browserStack.length > 1) {
      const newStack = browserStack.slice(0, -1);
      const parent = newStack[newStack.length - 1]!;
      setBrowserLoading(true);
      setError(null);
      try {
        const folders = await listSubfolders(parent.id, token);
        setBrowserStack(newStack);
        setBrowserFolders(folders);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setBrowserLoading(false);
      return;
    }

    // No history → fetch the actual parent of the current folder.
    const current = browserStack[0];
    if (!current || current.id === "root") return;
    setBrowserLoading(true);
    setError(null);
    try {
      const info = await getFolderWithParentName(current.id, token);
      if (!info.parentId) {
        // Already at Drive root — nothing above.
        setBrowserLoading(false);
        return;
      }
      const parent: DriveFolder = { id: info.parentId, name: info.parentName ?? "Thư mục mẹ" };
      const folders = await listSubfolders(parent.id, token);
      setBrowserStack([parent]);
      setBrowserFolders(folders);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi lên thư mục mẹ");
    }
    setBrowserLoading(false);
  }, [token, browserStack]);

  /**
   * Lock in the destination folder. Normal mode → "ready" confirm screen.
   * Quick mode → remember this folder for future quick imports and fire the
   * import right away (this branch only runs on first-time quick setup).
   */
  const commitDestination = useCallback(async (folder: DriveFolder) => {
    setDestFolderId(folder.id);
    setDestFolderName(folder.name);
    if (quick && token && sourceFolderId) {
      writeQuickFolder(folder);
      setStep("importing");
      const ok = await runImport({
        token,
        sourceFolderId,
        destFolderId: folder.id,
        folderName: folderNameRef.current.trim() || undefined,
      });
      if (!ok) setStep("error");
      return;
    }
    setStep("ready");
  }, [quick, token, sourceFolderId, runImport]);

  const handleBrowserSelect = useCallback(() => {
    const current = browserStack[browserStack.length - 1];
    if (!current) return;
    if (browserStack.length >= 2) {
      localStorage.setItem(LAST_FOLDER_KEY, browserStack[browserStack.length - 2]!.id);
    } else {
      localStorage.setItem(LAST_FOLDER_KEY, current.id);
    }
    void commitDestination(current);
  }, [browserStack, commitDestination]);

  /** Tap a folder row → pick it directly as destination (no extra confirm). */
  const handleBrowserPick = useCallback((folder: DriveFolder) => {
    const parent = browserStack[browserStack.length - 1];
    if (parent) localStorage.setItem(LAST_FOLDER_KEY, parent.id);
    void commitDestination(folder);
  }, [browserStack, commitDestination]);

  // ── Step 5: Start background import (manual "ready" screen) ─────────────────
  const handleStartImport = useCallback(async () => {
    if (!token || !sourceFolderId || !destFolderId) return;
    // Read the latest folder name from the ref — ALWAYS current, even if
    // the React closure happens to be stale.
    await runImport({
      token,
      sourceFolderId,
      destFolderId,
      folderName: folderNameRef.current.trim() || undefined,
    });
  }, [token, sourceFolderId, destFolderId, runImport]);

  // ── Render ───────────────────────────────────────────────────────────────────
  const currentBrowserFolder = browserStack[browserStack.length - 1];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto rounded-[28px] bg-[#fbfaf6] p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
              {quick ? "⚡ Import nhanh" : "Import to Drive"}
            </p>
            <h3 className="mt-1 text-lg font-semibold text-ink-0 leading-tight">
              {photo.chatName}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink-0 transition-colors"
          >
            ×
          </button>
        </div>

        {/* ── Auth: silent refresh in progress ── */}
        {step === "auth-checking" && (
          <div className="flex items-center justify-center gap-3 py-6">
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="animate-spin text-ink-2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            <span className="text-sm text-ink-2">Đang kiểm tra phiên Google…</span>
          </div>
        )}

        {/* ── Auth: silent refresh failed, show manual sign-in ── */}
        {step === "auth" && (
          <div className="space-y-4">
            <p className="text-sm text-ink-2">
              Phiên Google đã hết hạn. Đăng nhập lại để import thư mục <strong>compressed file</strong> vào Drive của bạn.
            </p>
            <button
              onClick={() => { void handleAuth(); }}
              className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-80"
            >
              Đăng nhập Google
            </button>
            {error && (
              <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>
        )}

        {/* ── Detecting ── */}
        {step === "detecting" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Spinner />
            <p className="text-sm text-ink-2">Đang tìm thư mục compressed...</p>
          </div>
        )}

        {/* ── Importing (quick mode, no confirm screen) ── */}
        {step === "importing" && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Spinner />
            <p className="text-sm text-ink-2 text-center">
              Đang import vào 📁 <strong className="text-ink-0">{destFolderName || "thư mục"}</strong>…
            </p>
          </div>
        )}

        {/* ── Confirm source folder ── */}
        {step === "confirm-folder" && (
          <div className="space-y-4">
            {foundAuto ? (
              <>
                <div className="rounded-[18px] border border-surface-2 bg-white px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1">
                    Thư mục nguồn
                  </p>
                  <p className="text-sm font-medium text-ink-0">📁 {sourceFolderName}</p>
                </div>
                <button
                  onClick={handleConfirmAuto}
                  className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white hover:opacity-80 transition-opacity"
                >
                  Dùng thư mục này →
                </button>
              </>
            ) : (
              <>
                <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 space-y-1.5">
                  <p className="font-medium">Không tự nhận diện được thư mục &quot;compressed&quot;.</p>
                  {availableSubfolders.length > 0 ? (
                    <p>Chọn 1 trong các thư mục con bên dưới:</p>
                  ) : (
                    <p>Dán link thư mục thủ công bên dưới:</p>
                  )}
                  {photo.driveLink?.trim() && (
                    <a
                      href={photo.driveLink.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-medium underline underline-offset-2 break-all hover:opacity-80"
                    >
                      Mở folder gốc trên Drive ↗
                    </a>
                  )}
                </div>

                {/* Subfolder picker — shown when listSubfolders returned > 0 */}
                {availableSubfolders.length > 0 && (
                  <div className="rounded-[18px] border border-surface-2 overflow-hidden bg-white">
                    <div className="max-h-[60vh] overflow-y-auto divide-y divide-surface-2">
                      {availableSubfolders.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => handlePickSubfolder(f)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-ink-0 transition-colors hover:bg-surface-1 active:bg-surface-2"
                        >
                          <span className="text-base">📁</span>
                          <span className="flex-1 truncate font-medium">{f.name}</span>
                          <span className="text-base text-ink-3">›</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual link fallback — collapsed under a divider when picker is shown */}
                <div className={availableSubfolders.length > 0 ? "pt-1" : ""}>
                  {availableSubfolders.length > 0 && (
                    <p className="mb-1.5 text-[11px] uppercase tracking-wider text-ink-3">
                      hoặc dán link thủ công
                    </p>
                  )}
                  <input
                    type="url"
                    value={manualLink}
                    onChange={(e) => setManualLink(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    className="w-full rounded-[14px] border border-surface-2 bg-white px-4 py-3 text-sm text-ink-0 placeholder:text-ink-3 outline-none transition-colors focus:border-ink-2"
                  />
                </div>

                {error && <p className="text-xs text-red-500">{error}</p>}

                {manualLink.trim() && (
                  <button
                    onClick={handleManualConfirm}
                    className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-80"
                  >
                    Xác nhận link →
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ── In-app folder browser ── */}
        {step === "folder-browser" && (
          <div className="space-y-3">
            {quick && (
              <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                Chọn <strong>thư mục mặc định</strong> cho ⚡ Import nhanh — sẽ được nhớ cho những lần sau và import luôn vào đây.
              </div>
            )}
            <div className="flex items-center gap-2 min-w-0">
              {currentBrowserFolder && currentBrowserFolder.id !== "root" && (
                <button
                  onClick={() => { void handleBrowserBack(); }}
                  disabled={browserLoading}
                  className="flex-shrink-0 text-sm text-ink-2 hover:text-ink-0 disabled:opacity-40 transition-colors"
                >
                  ‹ Lên thư mục mẹ
                </button>
              )}
              <span className="text-sm font-semibold text-ink-0 truncate">
                📁 {currentBrowserFolder?.name ?? "My Drive"}
              </span>
            </div>

            <button
              onClick={handleBrowserSelect}
              disabled={browserLoading || !currentBrowserFolder}
              className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:opacity-30 transition-opacity"
            >
              Chọn “{currentBrowserFolder?.name ?? "thư mục này"}” ✓
            </button>
            <p className="text-[11px] text-ink-3 text-center -mt-1">
              Tap tên folder = lưu vào đó · “Mở ›” = xem các thư mục bên trong
            </p>

            <div className="rounded-[18px] border border-surface-2 overflow-hidden">
              {browserLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : browserFolders.length === 0 ? (
                <p className="text-sm text-ink-3 text-center py-5">Không có thư mục con</p>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-surface-2">
                  {browserFolders.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center w-full text-sm text-ink-0"
                    >
                      <button
                        onClick={() => handleBrowserPick(f)}
                        className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-surface-1 active:bg-surface-2 transition-colors"
                      >
                        <span className="text-base">📁</span>
                        <span className="flex-1 truncate">{f.name}</span>
                      </button>
                      <button
                        onClick={() => { void handleBrowserEnter(f); }}
                        aria-label={`Mở ${f.name}`}
                        className="flex h-full items-center gap-1 px-3.5 py-3 text-xs font-medium text-ink-2 hover:bg-surface-1 active:bg-surface-2 transition-colors border-l border-surface-2"
                      >
                        Mở
                        <span className="text-base leading-none">›</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        )}

        {/* ── Ready: confirm & start ── */}
        {step === "ready" && (
          <div className="space-y-4">
            <div className="rounded-[18px] border border-surface-2 bg-white px-4 py-3 space-y-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-0.5">Nguồn</p>
                <p className="text-sm font-medium text-ink-0 truncate">📁 {sourceFolderName}</p>
              </div>
              <div className="border-t border-surface-2" />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-0.5">Đích</p>
                <p className="text-sm font-medium text-ink-0 truncate">📁 {destFolderName}</p>
              </div>
              <div className="border-t border-surface-2" />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1">Tên thư mục</p>
                <div className="relative">
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => {
                      setFolderName(e.target.value);
                      folderNameRef.current = e.target.value;
                    }}
                    placeholder={photo.chatName}
                    className="w-full rounded-[10px] border border-surface-2 bg-[#fbfaf6] px-3 py-2 pr-9 text-sm text-ink-0 placeholder:text-ink-3 outline-none focus:border-ink-2 transition-colors"
                  />
                  {folderName && (
                    <button
                      type="button"
                      onClick={() => {
                        setFolderName("");
                        folderNameRef.current = "";
                      }}
                      aria-label="Xóa tên thư mục"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-ink-3 hover:bg-surface-2 hover:text-ink-0 transition-colors"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-[14px] bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-700">
              Import sẽ chạy nền trên server. Bạn sẽ nhận thông báo Discord khi xong — không cần chờ.
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}

            <button
              onClick={() => { void handleStartImport(); }}
              disabled={submitting}
              className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:opacity-50 transition-opacity"
            >
              {submitting ? "Đang khởi động..." : "Bắt đầu Import →"}
            </button>
          </div>
        )}

        {/* ── Error ── */}
        {step === "error" && (
          <div className="space-y-4">
            <div className="rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error ?? "Đã xảy ra lỗi"}
            </div>
            <button
              onClick={() => { setError(null); setStep("auth"); }}
              className="w-full rounded-[18px] border border-surface-2 bg-white py-3 text-sm font-medium text-ink-1 hover:bg-surface-1 transition-colors"
            >
              Thử lại
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-6 w-6 text-ink-2" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}
